import React from 'react';

interface InvalidOfferDisplayProps {
	onGoBack?: () => void;
	title?: string;
	message?: string;
}

const InvalidOfferDisplay: React.FC<InvalidOfferDisplayProps> = ({
	onGoBack,
	title = 'Invalid Offer',
	message = "Sorry, this custom offer is no longer valid or doesn't exist. Please contact our support team for assistance.",
}) => {
	const handleGoBack = () => {
		if (onGoBack) {
			onGoBack();
		} else {
			window.history.back();
		}
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center px-4 py-8 animate-in fade-in duration-700">
			<div className="max-w-md w-full">
				{/* Main error card */}
				<div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-red-100 animate-in slide-in-from-bottom-4 duration-1000 hover:shadow-3xl transition-all duration-300">
					{/* Gradient header */}
					<div className="bg-gradient-to-r from-red-500 via-orange-500 to-red-600 px-8 py-6 text-white relative overflow-hidden">
						{/* Animated background pattern */}
						<div className="absolute inset-0 opacity-10">
							<div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16" />
							<div className="absolute bottom-0 right-0 w-24 h-24 bg-white rounded-full translate-x-12 translate-y-12" />
						</div>

						<div className="relative text-center">
							{/* Icon */}
							<div className="mb-4">
								<div className="mx-auto w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
									<svg
										className="w-8 h-8 text-white"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
										/>
									</svg>
								</div>
							</div>

							<h2 className="text-2xl font-bold mb-2">{title}</h2>
							<p className="text-red-100 text-sm opacity-90">Offer validation failed</p>
						</div>
					</div>

					{/* Content */}
					<div className="p-8 text-center">
						<p className="text-gray-600 mb-8 leading-relaxed">{message}</p>

						{/* Action buttons */}
						<div className="space-y-4">
							<button
								onClick={handleGoBack}
								className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-6 rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-200 font-semibold text-lg"
							>
								Go Back
							</button>

							<button
								onClick={() => window.open('mailto:support@frontdesk.com', '_blank')}
								className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-xl hover:bg-gray-200 transition-colors duration-200 font-medium"
							>
								Contact Support
							</button>
						</div>

						{/* Additional help */}
						<div className="mt-8 pt-6 border-t border-gray-100">
							<div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
								<div className="flex items-center">
									<svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
										<path
											fillRule="evenodd"
											d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
											clipRule="evenodd"
										/>
									</svg>
									<span>Need help?</span>
								</div>
								<div className="flex items-center">
									<svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
										<path
											fillRule="evenodd"
											d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"
											clipRule="evenodd"
										/>
										<path
											fillRule="evenodd"
											d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"
											clipRule="evenodd"
										/>
									</svg>
									<span>Email us</span>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Trust indicators */}
				<div className="mt-8 text-center animate-in fade-in duration-1000 delay-700">
					<div className="flex items-center justify-center space-x-6 text-gray-400">
						<div className="flex items-center">
							<svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
								<path
									fillRule="evenodd"
									d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
									clipRule="evenodd"
								/>
							</svg>
							<span className="text-sm">Secure Platform</span>
						</div>
						<div className="flex items-center">
							<svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
								<path
									fillRule="evenodd"
									d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
									clipRule="evenodd"
								/>
							</svg>
							<span className="text-sm">24/7 Support</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default InvalidOfferDisplay;
